/**
 * Authenticated <img>
 *
 * The attachment download endpoint is `[Authorize]`, so a plain `<img src>` answers 401 —
 * the bytes are fetched through the axios client (which carries the bearer token) and shown
 * from an object URL instead.
 *
 * Shows the file at full size, so it does NOT resample: this is the detail panel and the
 * lightbox, where the operator is reading account numbers off a transfer slip. The caching and
 * the object-URL lifetime live in `useAuthedObjectUrl`, which also means the proof panel and the
 * lightbox showing the same file share one download instead of fetching it twice.
 */

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useAuthedObjectUrl } from "@/hooks/useAuthedObjectUrl";

/** Manual retries offered before the tile settles on the failure message. */
const MAX_RETRIES = 3;

interface AuthedImageProps {
  src: string;
  alt: string;
  className?: string;
}

export function AuthedImage({ src, alt, className }: AuthedImageProps) {
  const { objectUrl, isError, refetch } = useAuthedObjectUrl(src);
  const [retryCount, setRetryCount] = useState(0);

  // The budget is per file. It used to reset by itself, because the count was a dependency of the
  // fetch effect that `src` also drove; with the fetch in the hook, a component reused for a
  // different file would otherwise start with its retries already spent.
  useEffect(() => {
    setRetryCount(0);
  }, [src]);

  if (isError) {
    return (
      <div
        className={`${className} flex flex-col items-center justify-center gap-2 rounded-lg border text-xs text-muted-foreground`}
      >
        <span>Failed to load image</span>
        {retryCount < MAX_RETRIES && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-xs"
            onClick={() => {
              setRetryCount((count) => count + 1);
              refetch();
            }}
          >
            Retry
          </Button>
        )}
      </div>
    );
  }

  if (!objectUrl) {
    return (
      <div
        className={`${className} flex animate-pulse items-center justify-center rounded-lg border text-xs text-muted-foreground`}
      >
        Loading...
      </div>
    );
  }

  return <img src={objectUrl} alt={alt} decoding="async" className={className} />;
}
